import moment from "moment";
import React from "react";
import { connect } from "react-redux";

import { OTransportCompanies } from "shared/gol-api/types/flightSearch";
import { dayMonthFormat, hourMinuteFormat } from "shared/lib/datetime";
import { capitalizeOnlyFirstLetter } from "shared/lib/functions";
import { FlightSegment } from "shared/lib/golObjectTypes/SearchFlightResponse/Response";

import Img from "@components/FileServer/Img";
import FormattedMessage from "@components/UI/FormattedMessage";

import AirlineTitle from "../Formatters/AirlineTitle";
import AirportTitle from "../Formatters/AirportTitle";
import FormatDuration from "../Formatters/FormatDuration";

interface Props {
  underTransfer: boolean;
  oFlightSegment: FlightSegment;
  oTransportCompanies: OTransportCompanies;
  oAirplanes: any;
  lang: string;
  defaultCountry: string;
}

const FlightDetailSegment = (props: Props) => {
  const { oFlightSegment, oTransportCompanies, oAirplanes } = props;

  return (
    <div className="flight-detail-one-way-flight">
      <Img
        src="/static/images/ico-plane.svg"
        className="flight-detail-one-way-flight-icon"
        alt="plane icon"
      />
      <div
        className={`flight-detail-one-way-flight-wrapper ${
          props.underTransfer
            ? " flight-detail-one-way-flight-wrapper__padding-top"
            : ""
        }`}
      >
        <div className="flight-detail-one-way-flight-flight">
          <Img
            src="/static/images/ico-dot.svg"
            className="flight-detail-one-way-flight-dot"
            alt="dot icon"
          />
          <div className="flight-detail-one-way-flight-flight-text-image-wrapper">
            <div className="flight-detail-one-way-flight-flight-row">
              <div className="flight-detail-one-way-flight-flight-row-times">
                <b>
                  {moment(oFlightSegment.DepartureDateTime).format(
                    hourMinuteFormat(props.defaultCountry)
                  )}
                </b>{" "}
                |{" "}
                {moment(oFlightSegment.DepartureDateTime).format(
                  dayMonthFormat(props.defaultCountry)
                )}{" "}
                {capitalizeOnlyFirstLetter(
                  moment(new Date(oFlightSegment.DepartureDateTime))
                    .locale(props.lang)
                    .format("dddd")
                    .toUpperCase()
                )}{" "}
              </div>
              <div className="flight-detail-one-way-flight-flight-row-destination">
                <AirportTitle airportCode={oFlightSegment.OriginAirport} />
              </div>
            </div>
            <img
              src={
                oTransportCompanies[oFlightSegment.MarketingAirline] !==
                undefined
                  ? oTransportCompanies[oFlightSegment.MarketingAirline].LogoUrl
                      .$t
                  : null
              }
              className="flight-detail-one-way-flight-flight-text-image"
              alt="Airline logo"
            />
          </div>
        </div>
        <div className="flight-detail-one-way-flight-flight">
          <Img
            src="/static/images/ico-dot.svg"
            className="flight-detail-one-way-flight-dot"
            alt="dot icon"
          />
          <div className="flight-detail-one-way-flight-flight-text-image-wrapper">
            <div className="flight-detail-one-way-flight-flight-row">
              <div className="flight-detail-one-way-flight-flight-row-times">
                <b>
                  {moment(oFlightSegment.ArrivalDateTime).format(
                    hourMinuteFormat(props.defaultCountry)
                  )}
                </b>{" "}
                |{" "}
                {moment(oFlightSegment.ArrivalDateTime).format(
                  dayMonthFormat(props.defaultCountry)
                )}{" "}
                {capitalizeOnlyFirstLetter(
                  moment(new Date(oFlightSegment.ArrivalDateTime))
                    .locale(props.lang)
                    .format("dddd")
                    .toUpperCase()
                )}{" "}
              </div>
              <div className="flight-detail-one-way-flight-flight-row-destination">
                <AirportTitle airportCode={oFlightSegment.DestinationAirport} />
              </div>
            </div>
          </div>
        </div>
        <FormattedMessage id="Flight.flightDuration" />
        : <FormatDuration duration={oFlightSegment.JourneyDuration} />
        <br />
        <FormattedMessage id="Flight.airline" />
        :&nbsp;
        <b>
          <AirlineTitle airlineCode={oFlightSegment.MarketingAirline} />
        </b>
        <span>
          <span
            className="only-desktop-span"
            style={{ marginLeft: 5, marginRight: 5 }}
          >
            |
          </span>
          <br className="only-mobile" />
          <FormattedMessage id="Flight.flightNumber" />
          :&nbsp;{oFlightSegment.MarketingAirline}
          {oFlightSegment.FlightNumber} |{" "}
          <FormattedMessage id="Flight.flightClass" />
          :&nbsp;
          <b>
            {
              oFlightSegment.AvailableBookingClasses.AvailableBookingClass[0]
                .Code
            }
          </b>
          {oFlightSegment.OperatingAirline !==
            oFlightSegment.MarketingAirline && (
            <div>
              |
              <FormattedMessage id="FlightDetailSegment.operating" />:{" "}
              <b>
                <AirlineTitle airlineCode={oFlightSegment.OperatingAirline} />
              </b>
            </div>
          )}{" "}
          <br />
          {oAirplanes && oFlightSegment.PlaneType && (
            <>
              <FormattedMessage id="FlightDetailSegment.planeType" />
              :&nbsp;{oAirplanes[oFlightSegment.PlaneType]}
            </>
          )}
        </span>
        {oFlightSegment.TechnicalStops !== undefined && (
          <div className="flight-detail-segment-technical-stop">
            <FormattedMessage id="FlightDetailSegment.technicalStopAttention" />
            :{" "}
            <AirportTitle
              airportCode={
                oFlightSegment.TechnicalStops.TechnicalStop[0].Destination
              }
            />
            . <FormattedMessage id="FlightDetailSegment.technicalStopVisa" />
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  oTransportCompanies: state.search.oTransportCompanies,
  oAirports: state.search.airports,
  oAirplanes: state.search.airplanes,
  lang: state.requestorConfig.currentLanguage,
  defaultCountry:
    state.storage.frontendSettings?.dealerFrontendSettings?.defaultCountry ??
    "",
});

export default connect(mapStateToProps)(FlightDetailSegment);
